/**
 * Breadcrumb contestuale per il detail preventivo:
 *   Clienti › {Cliente} › {Progetto} › {Preventivo}
 *
 * Ogni segmento è cliccabile. Se il cliente non è collegato (customer_id NULL),
 * mostra solo Progetti › {Progetto} › {Preventivo}.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ChevronRight, FolderOpen, UserSquare2 } from 'lucide-react';

interface EstimateBreadcrumbProps {
  projectId: string;
  projectName?: string | null;
  estimateName: string;
}

const EstimateBreadcrumb: React.FC<EstimateBreadcrumbProps> = ({ projectId, projectName, estimateName }) => {
  // Carica il customer linked al progetto (se presente)
  const { data: linkedCustomer } = useQuery({
    queryKey: ['project-linked-customer', projectId],
    enabled: !!projectId,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data } = await (supabase as any)
        .from('projects')
        .select('customer_id, customers ( id, name )')
        .eq('id', projectId)
        .maybeSingle();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (data as any)?.customers ?? null;
    },
  });

  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-xs text-muted-foreground mb-3 flex-wrap">
      {linkedCustomer ? (
        <>
          <Link to="/customers" className="hover:text-foreground inline-flex items-center gap-1">
            <UserSquare2 className="h-3 w-3" /> Clienti
          </Link>
          <ChevronRight className="h-3 w-3" />
          <Link to={`/customers/${linkedCustomer.id}`} className="hover:text-foreground hover:underline">
            {linkedCustomer.name}
          </Link>
          <ChevronRight className="h-3 w-3" />
        </>
      ) : (
        <>
          <Link to="/projects" className="hover:text-foreground inline-flex items-center gap-1">
            <FolderOpen className="h-3 w-3" /> Progetti
          </Link>
          <ChevronRight className="h-3 w-3" />
        </>
      )}
      <Link to={`/projects/${projectId}`} className="hover:text-foreground hover:underline">
        {projectName ?? 'Progetto'}
      </Link>
      <ChevronRight className="h-3 w-3" />
      <span className="text-foreground font-medium truncate max-w-xs" title={estimateName}>
        {estimateName}
      </span>
    </nav>
  );
};

export default EstimateBreadcrumb;
